import { useRef, useState } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import { TESTIMONIALS } from "@/data/site";

/* ─── single testimonial card ──────────────────────────────────────────────── */
function TestimonialCard({ t, idx, testid, compact = false }) {
  return (
    <figure
      data-testid={testid}
      className={`flex flex-col justify-between h-full bg-white border border-zinc-200 rounded-2xl
        ${compact ? "p-6" : "p-7 lg:p-8"}
        hover:border-zinc-400 hover:shadow-xl hover:shadow-zinc-100/80 transition-all duration-300`}
    >
      {/* stars */}
      <div>
        <div className="flex items-center gap-0.5 mb-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          ))}
        </div>

        {/* quote */}
        <blockquote className={`font-display text-zinc-900 leading-snug tracking-tight ${compact ? "text-base" : "text-base md:text-lg"}`}>
          "{t.quote}"
        </blockquote>
      </div>

      {/* author */}
      <figcaption className="mt-6 pt-5 border-t border-zinc-100 flex items-center gap-3">
        {/* avatar initial */}
        <div className="w-9 h-9 rounded-full bg-zinc-900 text-white flex items-center justify-center font-display text-sm shrink-0 select-none">
          {t.author[0]}
        </div>
        <div>
          <p className="font-display text-sm text-zinc-900 tracking-tight leading-tight">{t.author}</p>
          <p className="text-zinc-500 text-xs mt-0.5">{t.role}</p>
          <p className="font-mono-tech text-[10px] text-zinc-400 tracking-[0.16em] uppercase mt-1">{t.project}</p>
        </div>
      </figcaption>
    </figure>
  );
}

/* ─── mobile swipe carousel ────────────────────────────────────────────────── */
function MobileCarousel() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const scrollTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const card = track.children[i];
    if (card) {
      track.scrollTo({ left: card.offsetLeft - 16, behavior: "smooth" });
      setActive(i);
    }
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const scrollCenter = track.scrollLeft + track.clientWidth / 2;
    let closest = 0;
    Array.from(track.children).forEach((child, i) => {
      const childCenter = child.offsetLeft + child.offsetWidth / 2;
      const closestCenter = track.children[closest].offsetLeft + track.children[closest].offsetWidth / 2;
      if (Math.abs(childCenter - scrollCenter) < Math.abs(closestCenter - scrollCenter)) {
        closest = i;
      }
    });
    setActive(closest);
  };

  return (
    <div className="md:hidden">
      {/* track */}
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 -mx-4 px-4"
        style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
      >
        {TESTIMONIALS.map((t, idx) => (
          <div key={t.author + idx} className="snap-center shrink-0 w-[82vw] max-w-[340px]">
            <TestimonialCard t={t} idx={idx} testid={`testimonial-${idx}`} compact />
          </div>
        ))}
      </div>

      {/* count + dots */}
      <div className="mt-3 flex items-center justify-between px-1">
        <p className="font-mono-tech text-xs text-zinc-400 tracking-widest">
          {String(active + 1).padStart(2, "0")} / {String(TESTIMONIALS.length).padStart(2, "0")}
        </p>
        <div className="flex items-center gap-1.5">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to testimonial ${i + 1}`}
              onClick={() => scrollTo(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? "w-6 bg-zinc-900" : "w-1.5 bg-zinc-300"}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            aria-label="Previous"
            onClick={() => scrollTo(Math.max(0, active - 1))}
            disabled={active === 0}
            className="w-8 h-8 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-500 disabled:opacity-30 hover:border-zinc-400 hover:text-zinc-900 transition-all"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <button
            aria-label="Next"
            onClick={() => scrollTo(Math.min(TESTIMONIALS.length - 1, active + 1))}
            disabled={active === TESTIMONIALS.length - 1}
            className="w-8 h-8 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-500 disabled:opacity-30 hover:border-zinc-400 hover:text-zinc-900 transition-all"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── desktop masonry 3-col grid ───────────────────────────────────────────── */
function DesktopGrid() {
  /* split into 3 columns for a masonry feel */
  const cols = [[], [], []];
  TESTIMONIALS.forEach((t, i) => cols[i % 3].push({ t, i }));

  return (
    <div className="hidden md:grid grid-cols-3 gap-4 items-start">
      {cols.map((col, ci) => (
        <div key={ci} className={`flex flex-col gap-4 ${ci === 1 ? "mt-6" : ""}`}>
          {col.map(({ t, i }) => (
            <Reveal key={t.author + i} delay={i * 0.05}>
              <TestimonialCard t={t} idx={i} testid={`testimonial-${i}`} />
            </Reveal>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ─── section ──────────────────────────────────────────────────────────────── */
export default function Testimonials({ index = "/05" }) {
  return (
    <section data-testid="testimonials-section" className="py-20 md:py-32 border-t border-zinc-200">
      <div className="container-tight">
        {/* header */}
        <div className="grid md:grid-cols-12 gap-6 mb-12 md:mb-16 items-end">
          <Reveal className="md:col-span-7">
            <SectionLabel index={index} testid="testimonials-label">What clients say</SectionLabel>
            <h2 className="mt-5 font-display text-4xl md:text-5xl lg:text-6xl text-zinc-900 tracking-tighter leading-[1.02]">
              Trust, in their
              <br /><span className="text-zinc-400">own words.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5">
            <p className="text-zinc-600 leading-relaxed md:text-right text-[15px]">
              We&apos;ve worked with founders, family businesses, and enterprises from
              Coimbatore to Muscat. Here&apos;s what they&apos;ve said.
            </p>
            <div className="mt-4 md:text-right">
              <span className="inline-flex items-center gap-2 font-mono-tech text-xs text-zinc-500 tracking-widest uppercase">
                <span className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                  ))}
                </span>
                {TESTIMONIALS.length} verified reviews
              </span>
            </div>
          </Reveal>
        </div>

        {/* mobile carousel */}
        <Reveal>
          <MobileCarousel />
        </Reveal>

        {/* desktop grid */}
        <DesktopGrid />
      </div>
    </section>
  );
}
